import { type MouseEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { galText } from "./accounts";
import type * as api from "./api";
import { memberText } from "./driverRoutes";
import { accountName } from "./fill";
import { cardPlace, portalTarget } from "./Hint";
import { go } from "./hooks";
import { LevelDot } from "./LevelDot";
import { useMedia } from "./Menu";
import { emptiedText, expectedLoad, fillText, stopShare, truckColour, windowText } from "./routePlan";
import {
  axisText,
  ICON_W,
  LEFT_PX,
  leftPx,
  minutesAtText,
  nowMinutes,
  placeBoxes,
  pxPerMin,
  type TimelineBox,
  timelineRow,
  timelineSpan,
  timelineTicks,
  xOf,
} from "./routeTimeline";
import { TOUCH } from "./syncBadge";
import { truckSvg } from "./truckLoad";
import { TruckLoadLine } from "./TruckLoadLine";

const AXIS_H = 30;
const ROW_H = 84;
const LANE_H = 52;
const BELOW_LINE = 24;
const NOW_MS = 60_000;

interface Open {
  truck: api.PlanTruck;
  box: TimelineBox;
  anchor: HTMLElement;
}

function StopCard({ open, base, onStay, onLeave, touch }: { open: Open; base: string; onStay: () => void; onLeave: () => void; touch: boolean }) {
  const card = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const [target] = useState(portalTarget);
  useLayoutEffect(() => {
    if (card.current) setPlace(cardPlace(open.anchor.getBoundingClientRect(), card.current.getBoundingClientRect(), { width: innerWidth, height: innerHeight }));
  }, [open]);
  const { truck, box } = open;
  const stop = truck.stops[box.at];
  const arrive = `Arrive ${stop.arrive} · ${minutesAtText(box)}`;
  const expected = stop.expected_gal === null ? null : `${expectedLoad(stop).text} expected`;
  const lines = box.kind === "dump" ? [arrive, "The truck is empty after the dump."] : [arrive, fillText(stop), emptiedText(stop) ?? expected, windowText(stop) && `Window ${windowText(stop)}`];
  const href = stop.account_id ? `${base}/accounts/${stop.account_id}` : null;
  const title = box.kind === "dump" ? `Truck ${truck.truck} · dump at the depot` : `Truck ${truck.truck} · stop ${box.number}`;
  return createPortal(
    <div ref={card} className="hint-card timeline-card" role="tooltip" data-testid="timeline-card" data-truck={truck.truck} data-seq={box.seq} style={place ?? { visibility: "hidden" }} onMouseEnter={onStay} onMouseLeave={onLeave}>
      <p>
        <strong>{title}</strong>
      </p>
      {box.kind === "stop" && (
        <p className="map-info-box-name">
          <LevelDot share={stopShare(stop)} small /> <span>{accountName(stop.number, stop.name)}</span>
        </p>
      )}
      <p className="sub">{lines.filter(Boolean).join(" · ")}</p>
      {box.load && truck.capacity ? <TruckLoadLine load={box.load} capacity={truck.capacity} /> : null}
      {touch && href && (
        <a href={href} onClick={go} data-testid="timeline-card-link">
          Open the account
        </a>
      )}
    </div>,
    target,
  );
}

/** The proposed routes on a time axis: one line for each truck, a box for each stop at its arrival (rule U-29). */
export function RouteTimeline({ base, view, drivers, people, timeZone }: { base: string; view: api.PlanView; drivers: Record<string, string>; people: api.RouteDay["drivers"]; timeZone?: string }) {
  const touch = useMedia(TOUCH);
  const scroll = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const [open, setOpen] = useState<Open | null>(null);
  const [now, setNow] = useState(() => new Date());
  const leave = useRef<ReturnType<typeof setTimeout>>();
  const trucks = view.plan?.trucks ?? [];
  const rows = trucks.map(timelineRow);
  const span = timelineSpan(rows);
  useEffect(() => {
    const box = scroll.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    watch.observe(box);
    return () => watch.disconnect();
  }, []);
  useEffect(() => {
    if (view.day !== view.today) return;
    const timer = setInterval(() => setNow(new Date()), NOW_MS);
    return () => clearInterval(timer);
  }, [view.day, view.today]);
  useEffect(() => {
    console.info(`plan timeline: ${rows.length} trucks, ${rows.reduce((n, row) => n + row.boxes.length, 0)} boxes, ${trucks.flatMap((t) => t.stops).filter((s) => s.service_min == null).length} with the planner formula`);
  }, [view]);
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    const away = (e: PointerEvent) => !open.anchor.contains(e.target as Node) && !(e.target as Element).closest?.(".timeline-card") && setOpen(null);
    document.addEventListener("keydown", key);
    document.addEventListener("pointerdown", away);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", away);
    };
  }, [open]);
  useEffect(() => () => clearTimeout(leave.current), []);
  const centred = useRef<string | null>(null);
  useEffect(() => {
    const box = scroll.current;
    if (!box || !span || view.day !== view.today || centred.current === view.day || box.scrollWidth <= box.clientWidth) return;
    centred.current = view.day;
    const left = leftPx(width);
    box.scrollLeft = Math.max(0, xOf(nowMinutes(new Date(), timeZone), span.from, pxPerMin(width, span, left), left) - (box.clientWidth + left) / 2);
  });
  if (!span || !rows.length) return null;
  const left = leftPx(width);
  const px = pxPerMin(width, span, left);
  const x = (minutes: number) => xOf(minutes, span.from, px, left);
  const chartWidth = Math.ceil(x(span.to) + 16);
  const layouts = rows.map((row) => placeBoxes(row.boxes, px, x));
  const rowHeights = layouts.map(({ lanes }) => ROW_H + (lanes - 1) * LANE_H);
  const height = AXIS_H + rowHeights.reduce((sum, h) => sum + h, 0);
  const nowAt = view.day === view.today ? nowMinutes(now, timeZone) : null;
  const showNow = nowAt !== null && nowAt >= span.from && nowAt <= span.to;
  const show = (truck: api.PlanTruck, box: TimelineBox) => (e: { currentTarget: HTMLElement }) => {
    clearTimeout(leave.current);
    setOpen({ truck, box, anchor: e.currentTarget });
  };
  const hide = () => {
    clearTimeout(leave.current);
    leave.current = setTimeout(() => setOpen(null), 150);
  };
  const tap = (truck: api.PlanTruck, box: TimelineBox) => (e: MouseEvent<HTMLElement>) => {
    if (touch || box.kind === "dump") {
      e.preventDefault();
      show(truck, box)(e);
      return;
    }
    go(e as MouseEvent<HTMLAnchorElement>);
  };
  const person = (truck: string) => {
    const found = people.find((p) => p.id === drivers[truck]);
    return found ? memberText(found) : "No driver";
  };
  return (
    <section className="plan-timeline" aria-labelledby="plan-timeline-title" data-testid="plan-timeline">
      <h3 id="plan-timeline-title">Timeline</h3>
      <p className="sub">Each box is a stop at its planned arrival. Its width is the time at the stop. The truck above a box shows the load after the stop.</p>
      <div ref={scroll} className="timeline-scroll" tabIndex={0} role="region" aria-label="Timeline of the proposed routes" data-testid="timeline-scroll">
        <div className={left < LEFT_PX ? "timeline-chart narrow" : "timeline-chart"} style={{ width: chartWidth, height, ["--timeline-left" as string]: `${left}px` }}>
          <div className="timeline-grid" aria-hidden="true">
            {timelineTicks(span).map((tick) => (
              <span key={tick.minutes} className={tick.minutes % 60 === 0 ? "timeline-tick hour" : "timeline-tick"} style={{ left: x(tick.minutes) }}>
                {tick.label && tick.minutes > span.from && <span className="timeline-tick-label">{axisText(tick.minutes)}</span>}
              </span>
            ))}
          </div>
          <div className="timeline-head" style={{ height: AXIS_H }}>
            <span className="timeline-truck sub">
              <span>{left < LEFT_PX ? "Truck" : "Truck · driver"}</span>
              <span>Gallons</span>
            </span>
          </div>
          {trucks.map((truck, index) => {
            const row = rows[index];
            const colour = truckColour(index);
            const rowH = rowHeights[index];
            const shiftY = rowH - BELOW_LINE;
            const driver = person(truck.truck);
            return (
              <div
                key={truck.truck}
                className="timeline-row"
                style={{ height: rowH, ["--truck" as string]: colour }}
                data-testid="timeline-row"
                data-truck={truck.truck}
                data-lanes={layouts[index].lanes}
              >
                <div className="timeline-truck">
                  <span className="timeline-truck-name">
                    <strong>{truck.truck}</strong>{" "}
                    <span className="sub timeline-driver" title={driver}>
                      {driver}
                    </span>
                  </span>
                  <span className="timeline-truck-gal">
                    {truck.gallons === null ? "–" : galText(truck.gallons)}
                    {truck.capacity !== null && <span className="sub">of {galText(truck.capacity)} truck</span>}
                  </span>
                </div>
                {row.start !== null && row.end !== null && (
                  <>
                    <span className="timeline-shift" style={{ left: x(row.start), width: x(row.end) - x(row.start), top: shiftY }} aria-hidden="true" />
                    <span className="timeline-shift-end start" style={{ left: x(row.start), top: shiftY - 9 }} title={`Shift start ${axisText(row.start)}`} aria-hidden="true" />
                    <span className="timeline-shift-end" style={{ left: x(row.end), top: shiftY - 9 }} title={`Back at the depot ${axisText(row.end)}`} aria-hidden="true" />
                  </>
                )}
                {layouts[index].placed.map(({ box, left: boxLeft, width: w, lane }) => {
                  const stop = truck.stops[box.at];
                  const mid = shiftY - lane * LANE_H;
                  const label =
                    box.kind === "dump"
                      ? `Truck ${truck.truck}, dump at the depot, arrive ${stop.arrive}`
                      : `Truck ${truck.truck}, stop ${box.number}, ${accountName(stop.number, stop.name)}, arrive ${stop.arrive}`;
                  const common = {
                    className: `timeline-box ${box.kind}${box.empty ? " empty" : ""}`,
                    style: { left: boxLeft, width: w, top: mid - 9 },
                    "aria-label": label,
                    "data-testid": box.kind === "dump" ? "timeline-dump" : "timeline-stop",
                    "data-seq": box.seq,
                    "data-minutes": Math.round(box.minutes),
                    "data-lane": lane,
                    onMouseEnter: touch ? undefined : show(truck, box),
                    onMouseLeave: touch ? undefined : hide,
                    onFocus: show(truck, box),
                    onBlur: hide,
                    onClick: tap(truck, box),
                  };
                  return (
                    <span key={box.seq}>
                      {lane > 0 && <span className="timeline-stem" style={{ left: boxLeft, top: mid + 9, height: shiftY - mid - 9 }} aria-hidden="true" />}
                      {box.load && (
                        <span
                          className={box.load.share > 1 ? "timeline-load truck-load over" : "timeline-load truck-load"}
                          style={{ left: boxLeft + w / 2 - ICON_W / 2, top: mid - 40 }}
                          aria-hidden="true"
                          data-testid="timeline-load"
                          data-share={box.load.share.toFixed(2)}
                          dangerouslySetInnerHTML={{ __html: truckSvg(box.load.share) }}
                        />
                      )}
                      {box.kind === "dump" || !stop.account_id ? (
                        <button type="button" {...common}>
                          {box.kind === "dump" ? "Dump" : box.number}
                        </button>
                      ) : (
                        <a href={`${base}/accounts/${stop.account_id}`} {...common}>
                          {box.number}
                        </a>
                      )}
                    </span>
                  );
                })}
              </div>
            );
          })}
          {showNow && (
            <span className="timeline-now" style={{ left: x(nowAt), top: AXIS_H - 6 }} data-testid="timeline-now" aria-hidden="true">
              <span>Now</span>
            </span>
          )}
        </div>
      </div>
      <ul className="timeline-key" aria-label="Timeline key">
        <li>
          <span className="truck-load" aria-hidden="true" dangerouslySetInnerHTML={{ __html: truckSvg(0.6) }} /> Truck load after the stop
        </li>
        <li>
          <span className="truck-load over" aria-hidden="true" dangerouslySetInnerHTML={{ __html: truckSvg(1.1) }} /> More than the truck holds
        </li>
        <li>
          <span className="timeline-key-box dump" aria-hidden="true" /> Dump at the depot (the load goes to 0)
        </li>
        <li>
          <span className="timeline-key-box empty" aria-hidden="true" /> Empty check (no oil expected)
        </li>
        {showNow && (
          <li>
            <span className="timeline-key-now" aria-hidden="true" /> Now
          </li>
        )}
      </ul>
      {open && <StopCard open={open} base={base} touch={touch} onStay={() => clearTimeout(leave.current)} onLeave={hide} />}
    </section>
  );
}
